// client/src/features/auth/errors.ts — the auth problem codes, as Romanian · AUTH-004.
//
// `AuthErrors.cs` and `MfaErrors.cs` say their kebab-case codes "become RFC 9457 type URIs and
// frontend i18n keys". They cannot become keys by string-building (04 §9.6.3 — only `enums.*`
// may be computed), so the mapping is a literal table: one `case` per code, each a t() the
// extractor can see. A code the table does not know falls back to the server's own `title`,
// which is already Romanian, and then to `errors.generic`.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/http';
import { toProblemDetails } from '@/api/problem-details';

const TYPE_PREFIX = 'https://proprietari360.ro/errors/';

/**
 * The 403 a first sign-in with a one-time password answers (D-2712): the password was right and
 * opens nothing until the person chooses their own. Named because the login form branches on it
 * rather than rendering it.
 */
export const PASSWORD_CHANGE_REQUIRED = 'password-change-required';

/** The rule code from a problem `type`, or undefined when it is not one of ours. */
export function problemCode(error: unknown): string | undefined {
  const type = toProblemDetails(error).type;
  return type?.startsWith(TYPE_PREFIX) ? type.slice(TYPE_PREFIX.length) : undefined;
}

export function useAuthErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown) => {
      // A rejected fetch: nothing came back at all.
      if (!(error instanceof ApiError)) return t('auth.errors.network');

      switch (problemCode(error)) {
        case 'invalid-credentials':
          return t('auth.errors.invalidCredentials');
        case 'account-locked':
          return t('auth.errors.accountLocked');
        case 'email-already-registered':
          return t('auth.errors.emailAlreadyRegistered');
        case 'organization-slug-unavailable':
          return t('auth.errors.organizationSlugUnavailable');
        case 'invalid-refresh-token':
          return t('auth.errors.invalidRefreshToken');
        case 'token-reuse-detected':
          return t('auth.errors.tokenReuseDetected');
        case 'not-an-organization-member':
          return t('auth.errors.notAMember');
        case 'no-active-membership':
          return t('auth.errors.noActiveMembership');
        case 'mfa-challenge-invalid':
          return t('auth.errors.mfaChallengeInvalid');
        case 'mfa-code-invalid':
          return t('auth.errors.mfaCodeInvalid');
        case 'mfa-too-many-attempts':
          return t('auth.errors.mfaTooManyAttempts');
        case 'mfa-already-enrolled':
          return t('auth.errors.mfaAlreadyEnrolled');
        case 'mfa-not-enrolled':
          return t('auth.errors.mfaNotEnrolled');
        case 'mfa-enrollment-not-started':
          return t('auth.errors.mfaEnrollmentNotStarted');
        case 'mfa-mandatory':
          return t('auth.errors.mfaMandatory');
        case 'mfa-method-unavailable':
          return t('auth.errors.mfaMethodUnavailable');
        case 'password-reset-token-invalid':
          return t('auth.errors.passwordResetTokenInvalid');
        // D-2712 — the first sign-in with a one-time password. The login form catches
        // `password-change-required` itself and asks for the new password instead of showing
        // this; the sentence is here for a screen that meets the code without that step.
        case PASSWORD_CHANGE_REQUIRED:
          return t('auth.errors.passwordChangeRequired');
        // D-2711 — right, and lapsed. The fix is a new sheet from the landlord, not a retry.
        case 'temporary-password-expired':
          return t('auth.errors.temporaryPasswordExpired');
        // D-2712 — the printed password typed again as the new one, which would leave the
        // landlord knowing the password the person keeps.
        case 'new-password-same-as-temporary':
          return t('auth.errors.newPasswordSameAsTemporary');
        // D-2750, D-2760 — the password was RIGHT and the address is not confirmed yet. Only the
        // person who knows the password ever reads this (a wrong one is still 401), which is why
        // the login may offer the resend right under it. The sentence is the server's.
        case 'email-not-verified':
          return t('auth.errors.emailNotVerified');
        // D-2752, D-2759 — forged, expired (48 h) or naming an address the account no longer
        // has: one answer for the three, as the reset link has. The server's title sends the
        // person to the login page for a new link; the confirmation page offers one itself, so
        // the table's sentence only says that one can be asked for.
        case 'email-verification-invalid':
          return t('auth.errors.emailVerificationInvalid');
        case 'rate-limited':
          return t('auth.errors.rateLimited');
        // D-2652, D-2674 — the recipient's allowance, which is NOT the per-caller limiter above
        // and must not be told as „prea multe încercări de pe această conexiune". On the auth
        // surface only „Trimite codul pe e-mail" can meet it (a rationed password reset answers
        // success on purpose), so the sentence is the MFA one: a code went out recently, it still
        // works, and a new one can be asked for shortly. The server's own title speaks of „trimiți
        // din nou" to a landlord; this reader is signing in, and is told what to do instead.
        case 'email-recipient-rate-limited':
          return t('auth.errors.emailCodeRateLimited');
        default: {
          // 429 from the limiter carries its own type; anything else shows what the server
          // said, which for the auth surface is already Romanian (AuthErrors.cs).
          if (error.status === 429) return t('auth.errors.rateLimited');
          return error.problem.title ?? error.problem.detail ?? t('errors.generic');
        }
      }
    },
    [t],
  );
}

/**
 * Whether the failure means the pending challenge is gone and the user has to start from the
 * password again (02 §6.5) — as opposed to a wrong code they may retry.
 */
export function challengeIsSpent(error: unknown): boolean {
  const code = problemCode(error);
  return code === 'mfa-challenge-invalid' || code === 'mfa-too-many-attempts';
}
